import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import { ListPerson } from "../../../List/schema/list";
import { renderCurrentScoreResult } from "../../../List/utils/allColumns";
import { LeadName } from "../../../List/components/CampaignOverview/components/Lead/components/LeadName";
import { JobTitle } from "../../../List/components/CampaignOverview/components/Lead/components/JobTitle";
import { OrgName } from "../../../List/components/CampaignOverview/components/Lead/components/OrgName";
import { OutboxSenderInfo } from "../../../List/components/CampaignOverview/components/Lead/components/OutboxSenderInfo";
import { LeadColorDots } from "../../../List/components/CampaignOverview/components/Lead/components/LeadColorDots";
import { LeadColorLabels } from "../../../List/components/CampaignOverview/components/Lead/components/LeadColorLabels";
import { StepFailureTag } from "../../../List/components/CampaignOverview/components/Lead/components/StepFailureTag";
import { LeadHoldTag } from "../../../List/components/CampaignOverview/components/Lead/components/LeadHoldTag";
import { DeepResearchStatusChip } from "../../../List/components/CampaignOverview/components/Lead/components/DeepResearchStatusChip";
import { useDeepResearchStatus } from "../../../List/components/CampaignOverview/components/Lead/utils/useDeepResearchStatus";
import { getLastSentDate, getPendingScheduleDate } from "../../pages/Outbox/outbox-category.helper";
import { CardChannelBadge } from "./CardChannelBadge";
import { LeadCardCompact } from "./LeadCardCompact";
import { LeadStatusChip } from "./LeadStatusChip";
import { StepProgressBar } from "./StepProgressBar";
import {
	getCardChannel,
	getCardProvenance,
	getLastOutboundAuthor,
	getLastOutboundMessage,
	getLeadScore,
	getPreviewText,
	formatCardTime,
	resolveCardCampaignConfig,
} from "./lead-card-compact.helper";
import {
	getOutboxStatusChip,
	getStepProgress,
	resolveOutboxBucket,
	resolveStepFailure,
} from "./outbox-card-status.helper";

interface OutboxLeadCompactProps {
	row: ListPerson;
	handleSetCurrentLead: (index: number) => void;
	todayStart: Date;
	setSelected?: (selected: number[]) => void;
	selected?: number[];
	isCollapsed?: boolean;
}

/**
 * Compact Outbox lead card (PREC-3850): status-bucket chip, message preview and
 * step-progress bar in the mockup's single-column layout, while keeping the
 * functional sub-components (sender switcher, color dots, CRM syncs, checkbox).
 */
export const OutboxLeadCompact: FunctionComponent<OutboxLeadCompactProps> = ({
	row,
	handleSetCurrentLead,
	todayStart,
	setSelected,
	selected,
	isCollapsed = false,
}) => {
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);
	const { t } = useTranslation();

	const rowIndex = inbox.indexOf(row);

	const bucket = useMemo(() => resolveOutboxBucket(row, todayStart), [row, todayStart]);
	const failure = useMemo(() => resolveStepFailure(row), [row]);
	const chip = getOutboxStatusChip(bucket);
	const steps = useMemo(() => getStepProgress(row, bucket), [row, bucket]);
	const channel = getCardChannel(row);
	const provenance = getCardProvenance(row);
	const lastOutbound = useMemo(() => getLastOutboundMessage(row), [row]);
	const lastOutboundAuthor = useMemo(() => getLastOutboundAuthor(row), [row]);
	const deepResearchStatus = useDeepResearchStatus(row);
	const campaignConfig = useMemo(() => resolveCardCampaignConfig(row), [row]);

	const time = useMemo(() => {
		if (bucket === "error") {
			return formatCardTime(failure?.failedAt);
		}

		if (bucket === "ended") {
			return formatCardTime(getLastSentDate(row));
		}

		if (bucket === "paused") {
			return "";
		}

		return formatCardTime(getPendingScheduleDate(row));
	}, [bucket, failure, row]);

	const score = getLeadScore(row);

	const scoreBadge = renderCurrentScoreResult({
		scoring: row.prospect?.scorePersonaCategoryResult,
		score,
	});

	if (isCollapsed) {
		return (
			<div className="flex flex-col items-center gap-1 py-2 w-full border-b border-border-light">
				{scoreBadge}
				<OutboxSenderInfo lastOutboundAuthor={lastOutboundAuthor} row={row} />
			</div>
		);
	}

	return (
		<LeadCardCompact
			checkbox={
				campaignConfig && setSelected ? (
					<Checkbox
						isChecked={selected?.includes(row.id) || false}
						onChange={(e) => {
							e.stopPropagation();
							if (selected?.includes(row.id)) {
								setSelected([...(selected || []).filter((id) => id !== row.id)]);
							} else {
								setSelected([...(selected || []), row.id]);
							}
						}}
					/>
				) : undefined
			}
			chips={
				<>
					{failure ? (
						<StepFailureTag failure={failure} t={t} />
					) : (
						chip && <LeadStatusChip label={t(chip.labelKey)} tone={chip.tone} />
					)}
					{/*
					 * A lead whose next step was moved out by an out-of-office reply, or stopped for a
					 * colleague, otherwise sits in Scheduled looking perfectly ordinary — the bucket chip
					 * above cannot express either (PREC-3894/PREC-3895).
					 */}
					<LeadHoldTag schedules={row.schedules} />
					{deepResearchStatus && <DeepResearchStatusChip compact status={deepResearchStatus} />}
				</>
			}
			company={<OrgName compact row={row} />}
			dimmed={
				row.schedules?.[0]?.active === false ||
				(row.list.campaignStatus !== CampaignStatus.RUNNING &&
					!row.schedules?.some((schedule) => schedule?.conversationId))
			}
			gutter={
				<>
					{scoreBadge}
					<CardChannelBadge channel={channel} />
					<OutboxSenderInfo lastOutboundAuthor={lastOutboundAuthor} row={row} />
					<LeadColorDots row={row} onReviewChat={() => handleSetCurrentLead(rowIndex)} />
				</>
			}
			isActive={currentLeadIndex === rowIndex}
			name={<LeadName row={row} />}
			preview={lastOutbound ? getPreviewText(lastOutbound.message) : undefined}
			provenance={{
				listName: provenance.listName,
				listTo: provenance.listTo,
				campaignLabel: provenance.campaignName ?? t("outbox.card.chatOutreach"),
			}}
			steps={
				steps ? (
					<StepProgressBar
						label={t(steps.labelKey, steps.labelParams) as string}
						segments={steps.segments}
					/>
				) : undefined
			}
			time={time}
			title={<JobTitle compact row={row} />}
			topLabels={<LeadColorLabels row={row} />}
			onClick={() => handleSetCurrentLead(rowIndex)}
		/>
	);
};
